import { useId } from 'react';
import { Search } from 'lucide-react';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { DateRangePicker } from './components/DateRangePicker';
import { SyncJobsTable } from './components/SyncJobsTable';
import { useSyncJobs } from './hooks/useSyncJobs';
import {
  parseIsoDate,
  useSyncJobsFiltersStore,
} from './store/syncJobsFiltersStore';

/**
 * Replaces:
 *   - `Modules/ARP/Component/Dashboard/Dashboard.razor`        (`@page "/dashboard-old"`)
 *   - `Modules/ARP/Component/Dashboard/SyncJobsList.razor`     (`TelerikGrid`)
 *   - `Modules/Shared/Templates/DateRangePicker.razor`         (`TelerikDateRangePicker`)
 */
export function SyncJobsDashboardPage() {
  const searchInputId = useId();

  const startDateIso = useSyncJobsFiltersStore((s) => s.startDate);
  const endDateIso = useSyncJobsFiltersStore((s) => s.endDate);
  const search = useSyncJobsFiltersStore((s) => s.search);
  const page = useSyncJobsFiltersStore((s) => s.page);
  const pageSize = useSyncJobsFiltersStore((s) => s.pageSize);
  const sortCol = useSyncJobsFiltersStore((s) => s.sortCol);
  const sortDir = useSyncJobsFiltersStore((s) => s.sortDir);

  const setDateRange = useSyncJobsFiltersStore((s) => s.setDateRange);
  const setSearch = useSyncJobsFiltersStore((s) => s.setSearch);
  const setPage = useSyncJobsFiltersStore((s) => s.setPage);
  const setPageSize = useSyncJobsFiltersStore((s) => s.setPageSize);
  const setSort = useSyncJobsFiltersStore((s) => s.setSort);
  const reset = useSyncJobsFiltersStore((s) => s.reset);

  const debouncedSearch = useDebouncedValue(search, 300);

  const startDate = parseIsoDate(startDateIso);
  const endDate = parseIsoDate(endDateIso);

  const { data, isLoading, isFetching, isError } = useSyncJobs({
    startDate,
    endDate,
    searchParam: debouncedSearch,
    page,
    pageSize,
    sortCol,
    sortDir,
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 border-b border-cool-gray-2 pb-5 md:flex-row md:items-end md:justify-between">
        <h1 className="text-2xl font-bold leading-9 text-black">Summary</h1>
        <DateRangePicker
          startDate={startDateIso}
          endDate={endDateIso}
          onApply={(s, e) => setDateRange(s, e)}
          onReset={reset}
        />
      </div>

      <div className="flex justify-end">
        <div className="relative w-full max-w-[260px]">
          <Search
            aria-hidden="true"
            size={16}
            className="absolute left-2 top-1/2 -translate-y-1/2 text-cool-gray-10"
          />
          <label htmlFor={searchInputId} className="sr-only">
            Search sync jobs
          </label>
          <input
            id={searchInputId}
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            className="block w-full border border-cool-gray-2 py-2 pl-8 pr-3 text-sm shadow-sm placeholder:text-cool-gray-10 focus:border-black focus:outline-none focus:ring-0"
          />
        </div>
      </div>

      <SyncJobsTable
        rows={data?.data ?? []}
        totalRecords={data?.totalRecords ?? 0}
        page={page}
        pageSize={pageSize}
        sortCol={sortCol}
        sortDir={sortDir}
        isLoading={isLoading || isFetching}
        isError={isError}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onSortChange={setSort}
      />
    </div>
  );
}
